import { SessionOrg, SessionUser } from '@/types/session.types';
import { IS_ETS_NEXT } from '@/utils/constants';
import { hasPermission } from '@/utils/hubPermissions/hasPermissions';

import ActiveDataManagementIcon from '@/public/images/nav-menu/active/icon-data-management.svg';
import ActiveIBankReportingIcon from '@/public/images/nav-menu/active/icon-ibank.svg';
import ActiveTableConnectorIcon from '@/public/images/nav-menu/active/icon-table-connector.svg';
import InactiveDataManagementIcon from '@/public/images/nav-menu/inactive/icon-data-management-inactive.svg';
import InactiveIBankReportingIcon from '@/public/images/nav-menu/inactive/icon-ibank-inactive.svg';
import InactiveTableConnectorIcon from '@/public/images/nav-menu/inactive/icon-table-connector-inactive.svg';

import { NavigationItem } from '@/ui/Layout/Sidebar/NavigationItems/navigationItems';
import {
  SHARED_WITH_ME_PAGE_NAME,
  SHARED_WITH_ME_PAGE_URL,
} from '@/go.gov/utils/quicksight/sharedWithMe/constants';

export interface ReportingFolder {
  id: number | string;
  folder_name: string;
  folder_description: string;
  folder_module: string;
  parent_folder?: number | null;
  reports: number[];
  reportIds: string[];
  label: string;
  specialConsideration?: (
    item: NavigationItem,
    user: unknown,
    org: unknown
  ) => boolean;
  items: ReportingFolder[];
  url?: string;
  folderId: number | string;
}

export const goGovNavigationItems = [
  {
    label: 'Reporting Configuration',
    permission: 'quicksightReportingConfiguration',
    icon: {
      src: InactiveDataManagementIcon,
      activeSrc: ActiveDataManagementIcon,
      alt: 'Reporting Configuration',
    },
    items: [
      {
        label: 'Folder Configuration',
        url: '/gogov/reporting-configuration/folder-configuration',
      },
      {
        label: 'Report Configuration',
        url: '/gogov/reporting-configuration/report-configuration',
      },
    ],
  },
  {
    label: 'Report Snapshots',
    url: '/gogov/report-snapshots',
    icon: {
      src: InactiveTableConnectorIcon,
      activeSrc: ActiveTableConnectorIcon,
      alt: 'Report Snapshots',
    },
  },
  {
    label: 'My Schedules',
    url: '/gogov/schedules',
    permission: 'goGovScheduling',
    icon: {
      src: InactiveTableConnectorIcon,
      activeSrc: ActiveTableConnectorIcon,
      alt: 'My Schedules',
    },
  },
  {
    label: 'Reporting Groups',
    url: '/gogov/dashboard-groups',
    icon: {
      src: InactiveIBankReportingIcon,
      activeSrc: ActiveIBankReportingIcon,
      alt: 'Reporting Groups',
    },
    specialConsideration: (
      item: NavigationItem,
      user: SessionUser,
      org: SessionOrg
    ): boolean => {
      return (
        hasPermission(user, 'goGovReportingGroups') &&
        hasPermission(org, 'goGovReportingGroups')
      );
    },
  },
  {
    label: 'QuickSight User Ingestion',
    url: '/gogov/quicksight-user-ingestion',
    icon: {
      src: InactiveDataManagementIcon,
      activeSrc: ActiveDataManagementIcon,
      alt: 'QuickSight User Ingestion',
    },
    specialConsideration: (
      item: NavigationItem,
      user: SessionUser,
      org: SessionOrg
    ): boolean => {
      return (
        IS_ETS_NEXT &&
        hasPermission(user, 'goGovQuicksightUserIngestion') &&
        hasPermission(org, 'goGovQuicksightUserIngestion')
      );
    },
  },
];

function mapReportingFolderToNavItem(folder: ReportingFolder): NavigationItem {
  return {
    label: folder.label ?? folder.folder_name,
    url: folder.url,
    items: folder.items.map(mapReportingFolderToNavItem),
  };
}

export function getSharedWithMeNavItem(): NavigationItem {
  return {
    label: SHARED_WITH_ME_PAGE_NAME,
    url: SHARED_WITH_ME_PAGE_URL,
    icon: {
      src: InactiveIBankReportingIcon,
      activeSrc: ActiveIBankReportingIcon,
      alt: SHARED_WITH_ME_PAGE_NAME,
    },
  };
}

export function getGoGovFolderNavItems(
  reportingFolders: ReportingFolder[]
): NavigationItem[] {
  return reportingFolders.map((folder) => ({
    label: folder.folder_name,
    icon: {
      src: InactiveIBankReportingIcon,
      activeSrc: ActiveIBankReportingIcon,
      alt: folder.label,
    },
    url: folder.url,
    items: [
      {
        label: 'Ad-Hoc Analysis',
        url: `/gogov/reports/ad-hoc-analysis`,
        specialConsideration: (
          _item: NavigationItem,
          user: SessionUser,
          _org: SessionOrg
        ): boolean => {
          return user.isAdhocQuickSightUser ?? false;
        },
      },
      ...folder.items.map(mapReportingFolderToNavItem),
    ],
  }));
}
